"use client";

import { runs } from "./checkrideV6DemoData";

function parse(text: string) {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.includes(":") && !l.startsWith("DETERMINATION"))
    .map((l) => {
      const [task, ...rest] = l.split(":");
      const verdict = rest.join(":").trim();
      return { task: task.trim(), verdict, sat: verdict.startsWith("SAT") };
    });
}

/**
 * The verdict, verbatim from each exam's own record. Shown with the failures intact --
 * including the fact that most UNSATs here are Skill elements, which a spoken exam
 * cannot prove. Hiding that would misrepresent what the product currently does.
 */
export default function V6Determination() {
  return (
    <section id="verdict" className="py-24 md:py-32 px-6 bg-white border-t border-hairline/60">
      <div className="max-w-4xl mx-auto">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-accent">
          The verdict
        </span>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">
          A result per area, with the reason attached
        </h2>
        <p className="mt-4 text-ink-soft leading-relaxed max-w-2xl">
          Not a score out of ten. The same structure a DPE uses &mdash; satisfactory or
          not, area by area, and when it is not, exactly which required element went
          unproved. Here are all three candidates&rsquo; actual results.
        </p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {runs.map((r) => {
            const rows = parse(r.determination);
            const sat = rows.filter((x) => x.sat).length;
            return (
              <div key={r.sessionId} className="rounded-2xl border border-hairline overflow-hidden lift">
                <div className="px-4 py-3.5 bg-ground-alt border-b border-hairline">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-semibold text-ink-soft capitalize">
                      {r.persona} candidate
                    </span>
                    <span className="text-xs text-ink-faint tabular-nums">
                      {sat}/{rows.length} satisfactory
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-ink-faint tabular-nums">
                    {r.questions} questions &middot; {r.itemsAsked} topics
                  </div>
                </div>
                <div className="divide-y divide-hairline/60">
                  {rows.map((x) => (
                    <div key={x.task} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${
                          x.sat ? "bg-emerald-500" : "bg-amber-500"
                        }`} />
                        <span className="text-[13px] text-ink capitalize">
                          {x.task.replace(/_/g, " ")}
                        </span>
                      </div>
                      {!x.sat && (
                        <p className="mt-1 ml-3.5 text-[11px] leading-snug text-ink-faint">
                          {x.verdict.replace(/^UNSAT\s*—\s*/, "")}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-hairline bg-ground-alt px-5 py-4">
          <p className="text-sm text-ink-soft leading-relaxed">
            <strong className="font-semibold text-ink">Read these honestly.</strong>{" "}
            Most of the unsatisfactory marks above say &ldquo;Skill elements
            unproved&rdquo; &mdash; and a spoken exam cannot prove a flying skill. Those
            areas are waiting on the flight portion, not on a wrong answer. The marks that
            reflect the conversation are the ones reading &ldquo;no Risk element
            proved&rdquo;, where the candidate genuinely never addressed the risk side of
            the topic.
          </p>
        </div>
      </div>
    </section>
  );
}
